import React, { useState, useMemo } from 'react';
import { Clock, Copy, Check } from 'lucide-react';

const INCREMENTS = [
  { label: '6-minute (1/10 hr)', value: 6 },
  { label: '10-minute (1/6 hr)', value: 10 },
  { label: '15-minute (1/4 hr)', value: 15 },
];

function parseTimeInput(input) {
  if (!input || input.trim() === '') return null;
  const trimmed = input.trim();

  // h:mm or hh:mm format
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    const hours = parseInt(colonMatch[1]);
    const minutes = parseInt(colonMatch[2]);
    if (minutes >= 60) return null;
    return hours * 60 + minutes;
  }

  // "Xh Ym" or "Xh" or "Ym" format
  const hMatch = trimmed.match(/^(\d+)\s*h(?:ours?)?(?:\s+(\d+)\s*m(?:in(?:utes?)?)?)?$/i);
  if (hMatch) {
    const hours = parseInt(hMatch[1]);
    const minutes = hMatch[2] ? parseInt(hMatch[2]) : 0;
    return hours * 60 + minutes;
  }

  const mMatch = trimmed.match(/^(\d+)\s*m(?:in(?:utes?)?)?$/i);
  if (mMatch) {
    return parseInt(mMatch[1]);
  }

  // Plain number — treat as minutes
  const num = parseFloat(trimmed);
  if (!isNaN(num) && num >= 0) {
    return Math.round(num);
  }

  return null;
}

const BillingUnitConverter = () => {
  const [timeInput, setTimeInput] = useState('');
  const [increment, setIncrement] = useState(6);
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => {
    const totalMinutes = parseTimeInput(timeInput);
    if (totalMinutes === null || totalMinutes < 0) return null;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const exactDecimal = totalMinutes / 60;

    // Round up to nearest increment
    const incrementsCount = Math.ceil(totalMinutes / increment);
    const roundedMinutes = incrementsCount * increment;
    const roundedDecimal = roundedMinutes / 60;
    const roundedHours = Math.floor(roundedMinutes / 60);
    const roundedMins = roundedMinutes % 60;

    return {
      totalMinutes,
      hours,
      minutes,
      exactDecimal,
      incrementsCount,
      roundedMinutes,
      roundedDecimal,
      roundedHours,
      roundedMins,
    };
  }, [timeInput, increment]);

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result.roundedDecimal.toFixed(2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time Spent</label>
          <input
            type="text"
            value={timeInput}
            onChange={(e) => setTimeInput(e.target.value)}
            placeholder="e.g. 1:25, 50, 2h 15m"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
          />
          <p className="text-xs text-yellow-600 mt-1">
            Accepts: 1:25, 85, 1h 25m, 25min
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Billing Increment</label>
          <select
            value={increment}
            onChange={(e) => setIncrement(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
          >
            {INCREMENTS.map((inc) => (
              <option key={inc.value} value={inc.value}>{inc.label}</option>
            ))}
          </select>
        </div>
      </div>

      {result && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
          <div className="flex items-start justify-between">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Billable Time (rounded up)</p>
                <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Clock className="h-6 w-6 text-yellow-600" />
                  {result.roundedDecimal.toFixed(2)} hours
                </p>
              </div>
              <div className="flex gap-8 flex-wrap">
                <div>
                  <p className="text-sm text-gray-600">Actual Time</p>
                  <p className="text-lg font-semibold text-gray-700">
                    {result.hours > 0 && `${result.hours}h `}{result.minutes}m ({result.exactDecimal.toFixed(2)} hrs)
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Rounded To</p>
                  <p className="text-lg font-semibold text-gray-700">
                    {result.roundedHours > 0 && `${result.roundedHours}h `}{result.roundedMins}m ({result.incrementsCount} unit{result.incrementsCount !== 1 ? 's' : ''})
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleCopy}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Copy billable hours"
            >
              {copied ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingUnitConverter;
